// File: clientapp/src/pages/AssistantPage.jsx
import './AssistantPage.css';
import React, { useState, useRef, useEffect } from 'react';
import {
    Box, TextField, Typography, Paper, List, ListItem, ListItemText,
    IconButton, InputAdornment, Tooltip, Drawer, ListItemButton,
    Divider, Button, ListItemSecondaryAction
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ThumbUpAltOutlined from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlined from '@mui/icons-material/ThumbDownAltOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MenuIcon from '@mui/icons-material/Menu';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkEmoji from 'remark-emoji';
import remarkFootnotes from 'remark-footnotes';
import AddIcon from '@mui/icons-material/Add';
import { startChatConnection } from '../services/signalrService';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import CircularProgress from '@mui/material/CircularProgress';
import {
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import embed from 'vega-embed';

export function cleanMarkdown(markdown: string): string {
    return markdown
        .replace(/\r\n/g, '\n')                   // Normalize line endings
        .replace(/\n{3,}/g, '\n\n')               // Collapse excessive newlines
        .replace(/([^\n])\n(?=[^\n])/g, '$1  \n') // Turn single newlines into Markdown line breaks
        .replace(/[ \t]+$/gm, '')                 // Trim trailing spaces
        .trim();
}

export default function AssistantPage() {
    const [sessions, setSessions] = useState([]);
    const [session, setSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [initialPrompt, setInitialPrompt] = useState('');
    const [feedbackTexts, setFeedbackTexts] = useState({});
    const [searchText, setSearchText] = useState('');
    const [renamingSessionId, setRenamingSessionId] = useState(null);
    const [newTitle, setNewTitle] = useState('');
    const [showDrawer, setShowDrawer] = useState(false);

    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [collapsed, setCollapsed] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [sessionToDelete, setSessionToDelete] = useState(null);
    const [feedbackStates, setFeedbackStates] = useState({}); 

    const [stagedFiles, setStagedFiles] = useState([]);    

    const [profileGroup, setProfileGroup] = useState(null);

    const toggleSidebar = () => setCollapsed((prev) => !prev);

    const fileInputRef = useRef(null);
    const messagesEndRef = useRef(null);

    const chatInputRef = useRef(null);

    const [showUnsupportedDialog, setShowUnsupportedDialog] = useState(false);
    const [unsupportedFiles, setUnsupportedFiles] = useState([]);

    const loadSessions = async () => {
        const res = await fetch("/api/agentchat");
        if (res.ok) {
            const data = await res.json();
            setSessions(data);
        }
    };

    const [samplePrompts, setSamplePrompts] = useState([]);

    useEffect(() => {
        if (!session) {
            fetch('/api/agentchat/sampleprompts')
                .then(res => res.json())
                .then(setSamplePrompts)
                .catch(() => setSamplePrompts([]));
        }
    }, [session]);
        
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    const selectSession = async (id) => {
        const res = await fetch(`/api/agentchat/${id}`);
        const data = await res.json();
        setSession(data.session);
        setProfileGroup(data.profileGroup);
        setMessages(data.messages.map(m => ({
            id: m.chatMessageId,   // this is the real GUID
            role: m.role,
            text: m.text,
            createdAt: new Date(m.createdAt),
            complete: true
        })));

        // Focus the input after session is loaded and rendered
        setTimeout(() => {
            chatInputRef.current?.focus();
        }, 100);
    };
            
    const startSession = async (prompt) => {
        const res = await fetch(`/api/agentchat/start`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ InitialPrompt: prompt })
        });
        const data = await res.json();
        await selectSession(data.chatSessionId);
        loadSessions();
    };

    const sendMessage = async (text) => {
        if (!session) return;

        setLoading(true);
        setErrorMessage('');

        const fileIds = stagedFiles.map(f => f.persistentFileId);

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout

            const res = await fetch(`/api/agentchat/${session.chatSessionId}/message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Message: text, FileIds: fileIds }),
                signal: controller.signal
            });

            if (!res.ok) throw new Error(`Server returned ${res.status}`);
            setStagedFiles([]);
        } catch (err) {
            if (err.name === 'AbortError') {
                setErrorMessage('Request timed out. Please try again.');
            } else {
                setErrorMessage('Failed to send message. Please check your connection.');
            }
        } finally {
            setLoading(false);
        }
    };



    const cancelStream = async () => {
        if (!session) return;
        await fetch(`/api/agentchat/${session.chatSessionId}/cancel`, { method: 'POST' });
    };

    const SUPPORTED_EXTENSIONS = [
        ".c", ".cpp", ".cs", ".css", ".doc", ".docx", ".go", ".html", ".java", ".js", ".json",
        ".md", ".pdf", ".php", ".pptx", ".py", ".rb", ".sh", ".tex", ".ts", ".txt", 
        ".xlsx", ".xlsm", ".xls", ".xlsb", ".csv", ".tsv"
    ];

    const isSupportedFile = (file) => {
        const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
        return SUPPORTED_EXTENSIONS.includes(ext);
    };

    const handleFilesUpload = async (files) => {
        if (!session) return;

        setLoading(true);
        setErrorMessage('');

        const fileArray = Array.from(files);
        const validFiles = fileArray.filter(isSupportedFile);
        const rejectedFiles = fileArray.filter(f => !isSupportedFile(f));

        if (rejectedFiles.length > 0) {
            setUnsupportedFiles(rejectedFiles.map(f => f.name));
            setShowUnsupportedDialog(true);
        }

        try {
            const uploads = validFiles.map(async (f) => {
                const formData = new FormData();
                formData.append('file', f);
                const res = await fetch(`/api/agentchat/${session.chatSessionId}/upload`, {
                    method: 'POST',
                    body: formData
                });
                if (!res.ok) throw new Error(`Upload failed for ${f.name}`);
                return await res.json();
            });

            const results = await Promise.all(uploads);
            setStagedFiles((prev) => [...prev, ...results]);
        } catch (err) {
            setErrorMessage('Error uploading files. Please try again.');
        } finally {
            setLoading(false);
        }
    };



    const onDrop = (e) => {
        e.preventDefault();
        handleFilesUpload(e.dataTransfer.files);
    };

    const sendFeedback = async (messageId, isThumbsUp, comment) => {
        await fetch(`/api/agentchat/feedback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ messageId, isThumbsUp, feedbackText: comment })
        });
    };

    useEffect(() => { loadSessions(); }, []);
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        if (chatInputRef.current) {
            chatInputRef.current.focus();
        }
    }, [messages]);
    useEffect(() => {
        startChatConnection((sessionId, chunk, messageId) => {
            setMessages(prev => {
                const existingIndex = prev.findIndex(m => m.id === messageId);
                if (existingIndex !== -1) {
                    // Append chunk to existing message
                    const updated = [...prev];
                    updated[existingIndex] = {
                        ...updated[existingIndex],
                        text: updated[existingIndex].text + chunk,
                    };
                    return updated;
                } else {
                    // Insert new assistant message with real GUID
                    return [
                        ...prev,
                        {
                            id: messageId,
                            role: 'assistant',
                            text: chunk,
                            createdAt: new Date(),
                            complete: false
                        }
                    ];
                }
            });
        });
    }, []);



    const handleStart = async (e) => {
        e.preventDefault();
        if (!initialPrompt.trim()) return;
        await startSession(initialPrompt.trim());
        setInitialPrompt('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!chatInput.trim()) return;
        setMessages(prev => [...prev, { id: Date.now(), role: 'user', text: chatInput, createdAt: new Date() }]);
        await sendMessage(chatInput.trim());
        setChatInput('');
    };    

    const handleFeedback = async (messageId, isThumbsUp) => {
        const text = feedbackTexts[messageId] || '';

        await sendFeedback(messageId, isThumbsUp, text);

        setFeedbackStates((prev) => ({
            ...prev,
            [messageId]: {
                submitted: true,
                isThumbsUp
            }
        }));

        setFeedbackTexts((prev) => ({
            ...prev,
            [messageId]: ''
        }));
    };

    const handleRenameSession = async (id, event) => {
        if (event) event.stopPropagation(); //  Prevent parent click
        if (!newTitle.trim()) return;

        await fetch(`/api/agentchat/${id}/rename`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ newTitle })
        });

        // Update session list without selecting the renamed session
        setSessions((prev) =>
            prev.map((s) =>
                s.chatSessionId === id ? { ...s, title: newTitle } : s
            )
        );

        setRenamingSessionId(null);
        setNewTitle('');
    };

    const handleDeleteSession = async (id) => {
        try {
            await fetch(`/api/agentchat/${id}`, { method: 'DELETE' });

            if (session?.chatSessionId === id) {
                window.location.reload(); // or call selectSession(null)
            } else {
                await loadSessions(); // reload updated list
            }
        } catch (err) {
            console.error("Failed to delete session:", err);
        }
    };

    const filteredSessions = sessions.filter(s => s.title.toLowerCase().includes(searchText.toLowerCase()));

    const renderFeedbackControls = (messageId) => {
        const feedback = feedbackStates[messageId] || {};
        const comment = feedbackTexts[messageId] || '';
        const hasSubmitted = feedback.submitted;
        const isThumbsUp = feedback.isThumbsUp;

        const showComment = profileGroup >= 1 && profileGroup <= 4;

        return hasSubmitted ? (
            <Typography variant="caption" sx={{ mt: 1, opacity: 0.7 }}>
                Thanks for your feedback!
            </Typography>
        ) : (
            <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: 1,
                        width: '100%' // ensures children take full row when needed
                    }}
            >
                {showComment && (
                    <TextField
                        variant="outlined"
                        placeholder="How can we improve this response?"
                        value={comment}
                        onChange={(e) =>
                            setFeedbackTexts((prev) => ({
                                ...prev,
                                [messageId]: e.target.value,
                            }))
                        }
                        size="small"
                            sx={{
                                minWidth: { xs: '100px', sm: '180px' },
                                maxWidth: { xs: '140px', sm: '280px' },
                                flexGrow: 1,
                                '& .MuiInputBase-input': {
                                    fontSize: '0.75rem',
                                    py: '4px'
                                }
                            }}
                        disabled={hasSubmitted}
                    />
                )}
                <Tooltip title="Thumbs Up">
                    <IconButton
                        onClick={() =>
                            setFeedbackStates(prev => ({
                                ...prev,
                                [messageId]: { ...prev[messageId], isThumbsUp: true }
                            }))
                        }
                        disabled={hasSubmitted}
                        sx={{
                            p: 0.5,
                            fontSize: '0.75rem',
                            bgcolor: isThumbsUp === true ? 'success.main' : 'transparent',
                            color: isThumbsUp === true ? 'white' : 'inherit',
                            '&:hover': {
                                bgcolor: isThumbsUp === true ? 'success.dark' : 'action.hover',
                            }
                        }}
                    >
                        <ThumbUpAltOutlined fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Thumbs Down">
                    <IconButton
                        onClick={() =>
                            setFeedbackStates(prev => ({
                                ...prev,
                                [messageId]: { ...prev[messageId], isThumbsUp: false }
                            }))
                        }
                        disabled={hasSubmitted}
                        sx={{
                            p: 0.5,
                            fontSize: '0.75rem',
                            bgcolor: isThumbsUp === false ? 'error.main' : 'transparent',
                            color: isThumbsUp === false ? 'white' : 'inherit',
                            '&:hover': {
                                bgcolor: isThumbsUp === false ? 'error.dark' : 'action.hover',
                            }
                        }}
                    >
                        <ThumbDownAltOutlined fontSize="small" />
                    </IconButton>
                </Tooltip>

                {(isThumbsUp !== undefined || (showComment && comment.trim())) && (
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleFeedback(messageId, isThumbsUp)}
                        disabled={hasSubmitted}
                        sx={{
                            fontSize: '0.7rem',
                            minHeight: '28px',
                            py: '3px',
                            px: '10px'
                        }}
                    >
                        Submit
                    </Button>
                )}
            </Box>
        );
    };




    function VegaLite({ spec }) {
        const ref = React.useRef(null);
        React.useEffect(() => {
            if (!ref.current) return;
            try {
                embed(ref.current, spec, { actions: false, renderer: 'canvas' });
            } catch (e) {
                // swallow; we'll just show JSON below if needed
            }
        }, [spec]);

        return <div ref={ref} style={{ width: '100%', overflow: 'hidden' }} />;
    }





    return (
            
        <Box sx={{ display: 'flex', height: 'calc(100vh - 100px)' }}>
            {loading && (
                <Box sx={{
                    position: 'absolute',
                    top: 0, left: 0,
                    width: '100%', height: '100%',
                    bgcolor: 'rgba(255,255,255,0.7)',
                    zIndex: 2000,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <CircularProgress size={48} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Working...
                    </Typography>
                </Box>
            )}
            {
                errorMessage && (
                    <Box sx={{ width: '100%', textAlign: 'center', py: 1 }}>
                        <Typography variant="body2" color="error">{errorMessage}</Typography>
                    </Box>
                )
            }
                {isMobile ? (
                    <Drawer
                        anchor="left"
                        open={showDrawer}
                        onClose={() => setShowDrawer(false)}
                    >
                        <Box sx={{ width: 260, p: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>My Chats</Typography>
                            <TextField
                                fullWidth
                                placeholder="Search..."
                                size="small"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                sx={{ mb: 1 }}
                            />
                            <List dense>
                                {filteredSessions.map((s) => (
                                    <ListItemButton
                                        key={s.chatSessionId}
                                        onClick={() => {
                                            setShowDrawer(false);
                                            selectSession(s.chatSessionId);
                                        }}
                                    >
                                        <ListItemText primary={s.title} />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Box>
                    </Drawer>
                ) : (

                    
                    <Box sx={{
                        width: collapsed ? 60 : 260,
                        transition: 'width 0.3s ease',
                        flexShrink: 0,
                        borderRight: '1px solid #e0e0e0',
                        backgroundColor: '#fff',
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: collapsed ? 'center' : 'flex-start',
                    }}>
                        <Box sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            backgroundColor: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 1,
                            py: 1,
                            width: '100%'
                        }}>

                            {/* Collapse toggle icon */}
                            <IconButton onClick={toggleSidebar} sx={{ alignSelf: 'flex-end', m: 1 }}>
                                {collapsed ? <ChevronRight /> : <ChevronLeft />}
                            </IconButton>


                            {!collapsed && (
                                <>
                                    <Typography variant="subtitle1" sx={{ ml: 1, flexGrow: 1 }}>My Chats</Typography>
                                    <Tooltip title="New Chat">
                                        <IconButton onClick={() => {
                                            setSession(null);
                                            setMessages([]);
                                            setInitialPrompt('');
                                        }}><AddIcon /></IconButton>
                                    </Tooltip>
                                </>
                            )}
                        </Box>

                        {!collapsed && (
                            <Box sx={{ px: 1 }}>
                                <TextField
                                    fullWidth
                                    placeholder="Search..."
                                    size="small"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    sx={{ mb: 1 }}
                                />
                            </Box>
                        )}

                    {!collapsed && (
                        <List dense sx={{ overflowY: 'auto', flexGrow: 1 }}>
                            {filteredSessions.map((s) => (
                                <ListItemButton
                                    key={s.chatSessionId}
                                    selected={session?.chatSessionId === s.chatSessionId}
                                    onClick={() => selectSession(s.chatSessionId)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        paddingRight: 1,
                                        gap: 1,
                                        overflow: 'hidden',
                                        minHeight: 40,
                                        '&:hover .chat-actions': { opacity: 1 }
                                    }}
                                >
                                    {renamingSessionId === s.chatSessionId ? (
                                        <Box onClick={(e) => e.stopPropagation()} sx={{ flexGrow: 1 }}>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={newTitle}
                                                onChange={(e) => setNewTitle(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleRenameSession(s.chatSessionId);
                                                    if (e.key === 'Escape') setRenamingSessionId(null);
                                                }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRenameSession(s.chatSessionId, e);
                                                                }}
                                                            >
                                                                <SaveIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setRenamingSessionId(null);
                                                                }}
                                                            >
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </>
                                                    )
                                                }}
                                                sx={{ flexGrow: 1 }}
                                            />
                                        </Box>
                                    ) : (
                                        <>
                                            <Box
                                                sx={{
                                                    flexGrow: 1,
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    textOverflow: 'ellipsis',
                                                    pr: 1
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    noWrap
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        maxWidth: '140px'
                                                    }}
                                                    title={s.title}
                                                >
                                                    {s.title}
                                                </Typography>
                                            </Box>
                                            <Box
                                                className="chat-actions"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    opacity: 0,
                                                    transition: 'opacity 0.2s ease-in-out',
                                                    flexShrink: 0
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setRenamingSessionId(s.chatSessionId);
                                                        setNewTitle(s.title);
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSessionToDelete(s.chatSessionId);
                                                        setDeleteDialogOpen(true);
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </>
                                    )}
                                </ListItemButton>
                            ))}

                        </List>
                        )}
                    </Box>
                )}

                {/* Main Chat Area */}
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                {isMobile && (
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 50,
                            left: 8,
                            zIndex: 10,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            bgcolor: '#fff',
                            boxShadow: 1,
                            borderRadius: '12px',
                            p: 0.5,
                            gap: 0.5
                        }}
                    >
                        <IconButton
                            onClick={() => setShowDrawer(true)}
                            size="small"
                            sx={{ p: 0.5 }}
                        >
                            <ChevronRight fontSize="small" />
                        </IconButton>

                        <IconButton
                            onClick={() => {
                                setSession(null);
                                setMessages([]);
                                setInitialPrompt('');
                            }}
                            size="small"
                            sx={{ p: 0.5 }}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Box>
                )}



                    {!session ? (
                    <Box
                        component="form"
                        onSubmit={handleStart}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexGrow: 1,
                            px: 2
                        }}
                    >
                        <Typography
                            variant="h5"
                            sx={{ textAlign: 'center', mb: 2, fontSize: { xs: '1.25rem', sm: '1.75rem' } }}
                        >
                            How can I help?
                        </Typography>

                        <TextField
                            fullWidth
                            value={initialPrompt}
                            onChange={(e) => setInitialPrompt(e.target.value)}
                            placeholder="Ask anything..."
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton type="submit"><SendIcon /></IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                width: '100%',
                                maxWidth: '600px',
                                bgcolor: '#fff',
                                boxShadow: 1,
                                borderRadius: 1
                            }}
                        />

                        {!session && samplePrompts.length > 0 && (
                            <Box sx={{ mt: 3, textAlign: 'center', maxWidth: 600, mx: 'auto', px: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Try asking:
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {(isMobile ? samplePrompts.slice(0, 8) : samplePrompts).map((text, idx) => (
  <Button
    key={idx}
    variant="outlined"
    size="small"
    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
    onClick={() => setInitialPrompt(text)}
  >
    {text}
  </Button>
))}
                                </Box>
                            </Box>
                        )}

                    </Box>

                    ) : (
                        <>
                            <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, pt: 2 }}>
                                <List>
                                    {messages.map(msg => (
                                        <ListItem key={msg.id} alignItems="flex-start">
                                            <Paper sx={{
                                                p: 1.5,
                                                position: 'relative',
                                                bgcolor: msg.role === 'user' ? 'grey.100' : 'white',
                                                ml: msg.role === 'user' ? 'auto' : 0,
                                                mr: msg.role === 'assistant' ? 'auto' : 0,
                                                maxWidth: isMobile ? '100%' : '90%',
                                                minWidth: isMobile ? '260px' : '400px', // add this line
                                                borderRadius: 2,
                                                boxShadow: 1,
                                                overflowWrap: 'break-word'
                                            }}>
                                                <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                                                    <div className="markdown-body">
                                                        {
                                                            // console.log('?? Cleaned Markdown:', cleanMarkdown(msg.text))
                                                         }
                                                        

                                                        <ReactMarkdown
                                                            children={cleanMarkdown(msg.text)}
                                                            remarkPlugins={[remarkGfm, remarkEmoji, remarkFootnotes]}
                                                            components={{
                                                                code({ inline, className, children, ...props }) {
                                                                    const lang = (className || '').replace('language-', '');
                                                                    const raw = String(children).trim();

                                                                    // Render Vega-Lite charts from fenced blocks ```vega-lite
                                                                    if (!inline && (lang === 'vega-lite' || lang === 'vega')) {
                                                                        try {
                                                                            const spec = JSON.parse(raw);
                                                                            return <VegaLite spec={spec} />;
                                                                        } catch {
                                                                            // If JSON is invalid, fall back to plain code block
                                                                            return <pre {...props}>{raw}</pre>;
                                                                        }
                                                                    }
                                                                    return <code className={className} {...props}>{children}</code>;
                                                                }
                                                            }}
                                                        />

                                                    </div>
                                                </Typography>
                                                {msg.role === 'assistant' && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'row',
                                                            flexWrap: 'wrap',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: 0.5,
                                                            mt: 1
                                                        }}
                                                    >                                                      

                                                        {renderFeedbackControls(msg.id)}

                                                        <Tooltip title="Copy">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => navigator.clipboard.writeText(msg.text)}
                                                                className="copy-btn"
                                                                sx={{
                                                                    position: 'absolute',
                                                                    bottom: 8,
                                                                    right: 8,
                                                                    transition: 'opacity 0.2s ease-in-out'
                                                                }}
                                                            >
                                                                <ContentCopyIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>

                                                    </Box>

                                                    
                                                )}
                                                

                                            </Paper>
                                        </ListItem>
                                    ))}
                                    <div ref={messagesEndRef} />
                                </List>
                            </Box>

                            {/* Footer Chat Input */}
                            <Box component="form"
                                onSubmit={handleSubmit}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={onDrop}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 2,
                                    py: 1,
                                    borderTop: '1px solid #ccc',
                                    gap: 1,
                                    flexWrap: 'nowrap'
                                }}
                            >
                                <input
                                    type="file"
                                    multiple
                                    hidden
                                    accept={SUPPORTED_EXTENSIONS.join(",")}
                                    ref={fileInputRef}
                                    onChange={(e) => handleFilesUpload(e.target.files)}
                                />
                                <Tooltip title="Upload document">
                                    <Box sx={{ flexShrink: 0 }}>
                                        <IconButton onClick={() => fileInputRef.current.click()} disabled={loading}>
                                            <UploadFileIcon />
                                        </IconButton>
                                    </Box>
                                </Tooltip>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                    {stagedFiles.map((doc) => (
                                        <Paper key={doc.chatDocumentId} sx={{ px: 1, py: 0.5, display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="body2" sx={{ mr: 1 }}>{doc.fileName}</Typography>
                                            <IconButton size="small" onClick={() => setStagedFiles(prev => prev.filter(d => d.chatDocumentId !== doc.chatDocumentId))}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Paper>
                                    ))}
                                </Box>
                                <TextField
                                    inputRef={chatInputRef}
                                    multiline
                                    minRows={1}
                                    maxRows={4}
                                    value={chatInput}
                                    onChange={(e) => setChatInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSubmit(e);
                                        }
                                    }}
                                    placeholder="Type your message..."
                                    fullWidth
                                    size="small"
                                    disabled={loading}
                                />
                                <IconButton type="submit" color="primary" disabled={loading} sx={{ flexShrink: 0 }}>
                                    <SendIcon />
                                </IconButton>
                                {false && (
                                    <Tooltip title="Cancel Stream"><IconButton onClick={cancelStream}><CancelIcon /></IconButton></Tooltip>
                                )}
                            </Box>
                        </>
                    )}
            </Box>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Chat</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this chat session?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        color="error"
                        onClick={async () => {
                            if (sessionToDelete) {
                                await handleDeleteSession(sessionToDelete);
                                setSessionToDelete(null);
                                setDeleteDialogOpen(false);
                            }
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showUnsupportedDialog}
                onClose={() => setShowUnsupportedDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Unsupported File Types</DialogTitle>
                <DialogContent dividers>
                    <p>The following file(s) were skipped because they are not supported:</p>
                    <List dense>
                        {unsupportedFiles.map((name, idx) => (
                            <ListItem key={idx}>{name}</ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowUnsupportedDialog(false)} variant="contained" color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>


            </Box>

    );

}
